import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactionAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ClipboardCheck, CheckCircle, XCircle, MessageSquare, Loader2, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const Approvals = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [infoRequest, setInfoRequest] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await transactionAPI.getPending();
      setTransactions(res.data);
    } catch (err) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (txn) => {
    setProcessing(true);
    try {
      await transactionAPI.approve(txn.transaction_id);
      toast.success('Transaction approved');
      fetchData();
      setIsDetailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setProcessing(true);
    try {
      await transactionAPI.reject(selectedTxn.transaction_id, rejectReason);
      toast.success('Transaction rejected');
      fetchData();
      setIsRejectOpen(false);
      setIsDetailOpen(false);
      setRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoRequest.trim()) {
      toast.error('Please specify the information needed');
      return;
    }
    setProcessing(true);
    try {
      await transactionAPI.requestInfo(selectedTxn.transaction_id, infoRequest);
      toast.success('Information requested');
      fetchData();
      setIsInfoOpen(false);
      setIsDetailOpen(false);
      setInfoRequest('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request info');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const openDetail = (txn) => {
    setSelectedTxn(txn);
    setIsDetailOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="approvals-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Pending Approvals</h1>
          <p className="text-gray-500 mt-1">Review and approve payment requests</p>
        </div>

        {transactions.length > 0 ? (
          <div className="space-y-4">
            {transactions.map((txn) => (
              <Card key={txn.transaction_id} className="glass-card hover:border-blue-600/20 transition-colors">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-lg font-semibold text-gray-900">{txn.beneficiary_name}</p>
                        <p className="text-sm text-gray-500">{txn.department_name} • {txn.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(txn.amount)}</p>
                        <p className="text-xs text-gray-500">Submitted by {txn.submitted_by_name}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openDetail(txn)} className="border-gray-300 text-gray-600">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(txn)} className="bg-blue-900 hover:bg-blue-800" disabled={processing}>
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setSelectedTxn(txn); setIsRejectOpen(true); }} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {txn.info_response && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-blue-400 mb-1">Additional Info Provided:</p>
                      <p className="text-sm text-gray-900">{txn.info_response}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <CheckCircle className="w-16 h-16 text-blue-600/30 mb-4" />
              <p className="text-xl font-semibold text-gray-900 mb-2">All caught up!</p>
              <p className="text-gray-500">No pending approvals at the moment</p>
            </CardContent>
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Transaction Details</DialogTitle>
            </DialogHeader>
            {selectedTxn && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-500">Beneficiary</p><p className="text-gray-900">{selectedTxn.beneficiary_name}</p></div>
                  <div><p className="text-xs text-gray-500">Amount</p><p className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(selectedTxn.amount)}</p></div>
                  <div><p className="text-xs text-gray-500">Department</p><p className="text-gray-900">{selectedTxn.department_name}</p></div>
                  <div><p className="text-xs text-gray-500">Submitted By</p><p className="text-gray-900">{selectedTxn.submitted_by_name}</p></div>
                </div>
                <div><p className="text-xs text-gray-500">Description</p><p className="text-gray-900">{selectedTxn.description}</p></div>
                <div><p className="text-xs text-gray-500">Beneficiary Details</p><p className="text-gray-900 font-['JetBrains_Mono'] text-sm">{selectedTxn.beneficiary_details}</p></div>
                
                {selectedTxn.validation_results && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Validation Results</p>
                    {Object.entries(selectedTxn.validation_results.steps || {}).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between text-sm py-1">
                        <span className="text-gray-500">{key.replace(/_/g, ' ')}</span>
                        <Badge className={val.passed ? 'status-success' : 'status-error'}>{val.passed ? 'Passed' : 'Failed'}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => handleApprove(selectedTxn)} className="flex-1 bg-blue-900 hover:bg-blue-800" disabled={processing}>
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Approve
                  </Button>
                  <Button variant="outline" onClick={() => { setIsInfoOpen(true); }} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                    <MessageSquare className="w-4 h-4 mr-2" /> Request Info
                  </Button>
                  <Button variant="outline" onClick={() => setIsRejectOpen(true)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Reject Transaction</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection..." className="bg-gray-50 border-gray-300 text-gray-900 min-h-24" />
              <Button onClick={handleReject} className="w-full bg-red-500 hover:bg-red-600" disabled={processing}>
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Reject Transaction
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Request Info Dialog */}
        <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Request Additional Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Textarea value={infoRequest} onChange={(e) => setInfoRequest(e.target.value)} placeholder="What information do you need?" className="bg-gray-50 border-gray-300 text-gray-900 min-h-24" />
              <Button onClick={handleRequestInfo} className="w-full bg-blue-500 hover:bg-blue-600" disabled={processing}>
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Send Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Approvals;
